<script>
import { groceryMethods } from '@state/helpers';

export default {
  name: 'GroceryListItem',
  components: {},
  props: {
    isCategory: {
      type: Boolean,
      required: false,
    },
    isDeletable: {
      type: Boolean,
      required: false,
    },
    isSelectable: {
      type: Boolean,
      required: false,
    },
    item: {
      type: Object,
      required: true,
      default: () => {}
    },
  },
  data() {
    return {
      isSelected: false,
    };
  },
  computed: {
    itemNum() {
      const count = this.item.groceryItems.filter((item) => item.isChecked === false).length;
      return `${count} ${count === 1 ? 'item' : 'items'}`;
    },
    isLongName() {
      const charLimit = 19;

      if ((this.item.ingredient || {}).name) {
        return (this.item.ingredient || {}).name.length > charLimit;
      } else {
        return (this.item.name.length > charLimit);
      }
    },
  },
  mounted() {},
  methods: {
    ...groceryMethods,
    deleteItem() {
      this.$emit('delete-item', this.item);
    },
    selectItem() {
      if (this.isCategory) {
        this.isSelected = !this.isSelected;
        this.$emit('select-item', this.item);
      }
    },
    async toggleCheck() {
      if (!this.isCategory) {
        await this.updateGroceryListItem({
          id: this.item.id,
          isChecked: !this.item.isChecked,
        });
        this.setUpdateGroceryItem(this.item);
      }
    },
  },
};
</script>

<template>
  <div>
    <div
      :class="['grocery-list-item-wrapper', { open: isCategory && isSelected, 'sub-item': !isCategory }]"
      v-b-toggle="`accordion-${item.id}`"
    >
      <div
        :class="[
          'grocery-list-item',
          { custom: item.isCustom, category: isCategory, open: isCategory && isSelected },
        ]"
        @click="selectItem"
      >
        <div :class="['item-icon', { checked: item.isChecked }]" @click.stop="toggleCheck">
          <img class="icon-category" :src="item.imageUrl" v-if="isCategory && item.imageUrl !== 'null'" />
          <svg width="26" height="26" viewBox="0 0 26 26" xmlns="http://www.w3.org/2000/svg" v-bind:class="'icon-circle'" v-bind:svg-inline="''" v-if="isSelectable && !item.isChecked" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><circle cx="28" cy="31" r="12" fill="none" transform="translate(-15 -18)" stroke="#F7C11C" stroke-width="2"/></svg>
          <svg width="30" height="26" viewBox="0 0 30 26" xmlns="http://www.w3.org/2000/svg" v-bind:class="'icon-circle'" v-bind:svg-inline="''" v-if="isSelectable && item.isChecked" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><g transform="translate(1 1)" fill="none" fill-rule="evenodd"><circle stroke="#F7C11C" stroke-width="2" cx="12" cy="12" r="12"/><path d="M20 9l4-4" stroke="#FFF" stroke-width="7" fill="#D8D8D8" stroke-linecap="square"/><path stroke="#F7C11C" stroke-width="2.5" d="M6.884 10l6.094 5.339L25.196 3.737"/></g></svg>
        </div>

        <div class="item-name" :class="{ 'text-wrap': isLongName }">
          {{ (item.ingredient || {}).name || item.name }}
        </div>

        <span class="category-item-group" v-if="isCategory">
          <div class="item-count">
            <span>
              <span v-if="!isSelected">{{ itemNum }}</span>
              <span v-else>Close</span>
            </span>
          </div>
          <img src="@assets/images/icon-chevron-right.svg" v-if="isCategory && !isSelected" />
        </span>
        <div class="item-units" v-if="!isCategory">{{ item.count }}</div>
        <img class="icon-trash" src="@assets/images/icon-trash-o.svg" v-if="isDeletable" @click.stop="deleteItem" />
      </div>
    </div>

    <b-collapse class="grocery-list-detail-wrapper" :id="`accordion-${item.id}`" v-if="isCategory">
      <b-card>
        <BaseGroceryListDetail :data="item" v-if="isCategory" />
      </b-card>
    </b-collapse>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';

.grocery-list-item-wrapper {
  padding-left: 1.5rem;
  padding-right: 1.5rem;

  &.open {
    padding-left: 0;
    padding-right: 0;
  }

  &.sub-item {
    padding-left: 0;
    padding-right: 0;
  }
}

.grocery-list-item {
  align-items: center;
  background-color: $color-grocery-list-item-bg;
  border: 1px solid $color-grocery-list-item-border;
  border-radius: $base-border-radius-md;
  display: flex;
  height: 7rem;
  margin-bottom: 0.5rem;
  padding: 1.25em 1em 1.25em 1em;
  transition: 0.2s;
  will-change: transform;

  &:hover,
  &:active {
    cursor: pointer;
  }

  .item-icon {
    margin-right: 1.5rem;

    svg {
      circle,
      g path:last-child {
        stroke: $color-grocery-list-item-checkbox-stroke;
      }
    }

    &.checked {
      margin-right: 1.1rem;
    }

    img.icon-category {
      width: 60%;
    }

    img.icon-circle {
      left: 1.6rem;
      position: absolute;
      top: 1.8rem;
    }
  }

  &.category {
    .item-icon {
      flex: 1;
      margin-right: 0;
    }

    &.open {
      border-left: 0;
      border-radius: 0;
      border-right: 0;
      border-top: 0;
      margin-bottom: 0;

      .item-count span:last-child {
        font-size: 1.4rem;
        font-weight: bold;
        letter-spacing: -0.3px;
        line-height: 2.4rem;
      }
    }
  }

  .item-name {
    color: $color-grocery-list-item-text;
    flex: 3;
    font-size: 1.6rem;
    font-weight: 600;
    line-height: 1;

    &.text-wrap {
      height: 3.8rem;
      overflow: hidden;

      // iPhone 6 up
      @include media('md', null) {
        font-size: 1.7rem;
      }
    }
  }

  .item-count,
  .item-units {
    color: $color-grocery-list-item-text-alt;
    flex: 1.2;
    font-size: 1.4rem;
    padding: 0 1.1rem 0 0.5rem;
    text-align: right;
  }

  .category-item-group {
    display: inline-flex;
    line-height: 1.1;

    .fa-chevron-right {
      color: $color-grocery-list-item-text-alt;
      font-size: 1.6rem;
    }
  }

  .icon-trash {
    width: 2.6rem;
  }
}

.card {
  background-color: transparent;
  border: 0;

  .card-body {
    padding: 0;
  }
}

div[aria-expanded='true'] {
  background: $color-grocery-list-bg;
  height: 100%;
  overflow-y: scroll;
  position: absolute;
  top: 29.1rem;
  width: 100%;
  z-index: 1;
}

.grocery-list-detail-wrapper {
  background: transparent;
  height: 100%;
  overflow-y: scroll;
  position: absolute;
  top: 35.9rem;
  width: 100%;
  z-index: 1;
}
</style>
